import { useCallback } from 'react';
import {
  TravelerNote,
  TravelerNoteStatus,
} from '@marageti/z4-sdk/lib/people';
import useTravelerProfile from '../../hooks/use-traveler-profile';

type UseTravelerProfileNotesOptions = {
  travelerId: string | undefined;
  teamMemberId: string;
};

export default function useTravelerProfileNotes({
  travelerId,
  teamMemberId,
}: UseTravelerProfileNotesOptions) {
  const {
    travelerProfile,
    updateTravelerProfile,
    isSaving,
  } = useTravelerProfile(travelerId);

  const notes = travelerProfile?.travelerNotes ?? [];

  const addNote = useCallback(async (input: {
    title: string;
    content: string;
    createdBy: string;
    updatedBy: string;
    status: TravelerNoteStatus;
  }) => {
    if (!travelerProfile) return;
    const now = new Date().toISOString();
    const newNote: TravelerNote = {
      id: '',
      title: input.title,
      content: input.content,
      createdAt: now,
      createdBy: input.createdBy,
      updatedAt: now,
      updatedBy: input.updatedBy,
      status: input.status,
    };
    await updateTravelerProfile(
      { travelerNotes: [newNote, ...(travelerProfile.travelerNotes ?? [])] },
      'Could not save traveler notes.',
      'Note saved.',
    );
  }, [travelerProfile, updateTravelerProfile]);

  const updateNote = useCallback(async (
    id: string,
    updates: Partial<Pick<TravelerNote, 'title' | 'content' | 'updatedBy'>>,
  ) => {
    if (!travelerProfile) return;
    const now = new Date().toISOString();
    const next = travelerProfile.travelerNotes.map((n) => (n.id === id
      ? { ...n, ...updates, updatedAt: now }
      : n));
    await updateTravelerProfile(
      { travelerNotes: next },
      'Could not save traveler notes.',
      'Note saved.',
    );
  }, [travelerProfile, updateTravelerProfile]);

  const setNoteStatus = useCallback(async (id: string, status: TravelerNoteStatus) => {
    if (!travelerProfile) return;
    const now = new Date().toISOString();
    const next = travelerProfile.travelerNotes.map((n) => (n.id === id
      ? {
        ...n,
        status,
        updatedAt: now,
        updatedBy: teamMemberId,
      }
      : n));
    await updateTravelerProfile(
      { travelerNotes: next },
      'Could not save traveler notes.',
      'Note saved.',
    );
  }, [travelerProfile, updateTravelerProfile, teamMemberId]);

  return {
    travelerProfile,
    notes,
    addNote,
    updateNote,
    setNoteStatus,
    isSaving,
  };
}
